import React from 'react';
import { WorkItem, Appointment, JobWithNotes } from './workItemTypes';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useWorkNotes } from './hooks/useWorkNotes';
import { useAppointments } from './hooks/useAppointments';
import { groupWorkItemsByAssignee, sortAssignees } from './utils/workItemUtils';
import { WorkItemRow } from './components/WorkItemRow';
import { WorkItemDetails } from './components/WorkItemDetails';
import { AssigneeGroupHeader } from './components/AssigneeGroupHeader';
import './themes/WorkItemTable-MinimalGray.css';

export interface WorkItemTableProps {
  workItems: WorkItem[];
  jobs: JobWithNotes[];
  appointments: Appointment[];
  selectedWorkItem: WorkItem | null;
  onWorkItemSelect: (workItem: WorkItem) => void;
  onWorkItemUpdate?: (workItem: WorkItem) => void;
  onJobUpdate?: (job: JobWithNotes) => void;
  onAppointmentUpdate?: (appointment: Appointment) => void;
  onWorkItemReorder?: (workItems: WorkItem[]) => void;
  onWorkItemReassign?: (workItemId: string, newAssignee: string) => void;
  currentUser?: string;
}

export const WorkItemTable: React.FC<WorkItemTableProps> = ({
  workItems,
  jobs,
  appointments,
  selectedWorkItem,
  onWorkItemSelect,
  onWorkItemUpdate,
  onJobUpdate,
  onAppointmentUpdate,
  onWorkItemReorder,
  onWorkItemReassign,
  currentUser
}) => {
  // Initialize hooks
  const dragAndDropHandlers = useDragAndDrop(
    workItems,
    jobs,
    appointments,
    onJobUpdate || (() => {}),
    onAppointmentUpdate || (() => {}),
    onWorkItemReorder || (() => {}),
    onWorkItemReassign || (() => {})
  );

  const workNotesHandlers = useWorkNotes({
    onJobUpdate,
    currentUser
  });

  const appointmentsHandlers = useAppointments({
    selectedWorkItem,
    onJobUpdate,
    onAppointmentUpdate,
    currentUser
  });

  // Group and sort work items by assignee
  const groupedWorkItems = groupWorkItemsByAssignee(workItems, jobs, appointments);
  const sortedAssignees = sortAssignees(Object.keys(groupedWorkItems), currentUser);

  return (
    <div className="workitem-list">
      {sortedAssignees.map((assignee: string) => (
        <div key={assignee}>
          <AssigneeGroupHeader
            assignee={assignee}
            workItemCount={groupedWorkItems[assignee].length}
            currentUser={currentUser}
            isDragOverAssignee={dragAndDropHandlers.dragOverAssignee === assignee}
            dragHandlers={dragAndDropHandlers}
          />
          
          <div className="workitem-list-header">
            <span>ID</span>
            <span>Title</span>
            <span>Customer</span>
            <span>Status</span>
          </div>
          
          {groupedWorkItems[assignee].map((workItem) => {
            const isDragging = dragAndDropHandlers.draggedWorkItem === workItem.id;
            const isDragOver = dragAndDropHandlers.dragOverWorkItem === workItem.id;
            const dragOverClass = isDragOver ? 
              (workItems.find(w => w.id === dragAndDropHandlers.draggedWorkItem && 
                groupedWorkItems[assignee].some(wi => wi.id === w.id)) ? 
                'drag-over' : 'drag-over-reassign') : '';
            
            return (
              <React.Fragment key={workItem.id}>
                <WorkItemRow
                  workItem={workItem}
                  isSelected={selectedWorkItem?.id === workItem.id}
                  isDragging={isDragging}
                  dragOverClass={dragOverClass}
                  onWorkItemSelect={onWorkItemSelect}
                  dragHandlers={dragAndDropHandlers}
                />
                
                {/* Expandable details section for selected work item */}
                {selectedWorkItem?.id === workItem.id && (
                  <WorkItemDetails
                    workItem={workItem}
                    workItems={workItems}
                    jobs={jobs}
                    appointments={appointments}
                    workNotesHandlers={workNotesHandlers}
                    appointmentsHandlers={appointmentsHandlers}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
};